/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { darkModeAware, withStyles } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import Error from '../Error/Error';
import styles from './NumberInput.styles';
import LabelText from '../LabelText/LabelText';
import InputComponent from '../Input/InputComponent';

let uniqueId = 0;

const NumberInput = ({
  labelText,
  classes,
  className,
  labelSpanClass,
  screenReaderLabel,
  inputClass,
  error,
  errors,
  htmlId,
  dark,
  optional,
  min,
  max,
  step,
  inline,
  value,
  onChange,
  disabled,
  ...props
}) => {
  const errorMessage = error || errors?.[0];
  const isAtMax = (max === value && (max !== undefined && max !== null));
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `number-input-${uniqueId++}`, [htmlId]);
  const aboveOrEqualMin = (val) => (
    min || min === 0 ? min <= val : true
  );
  const belowOrEqualMax = (val) => (
    max || max === 0 ? max >= val : true
  );
  const getStep = () => (
    // eslint-disable-next-line no-restricted-globals
    isNaN(step) ? 1 : step
  );

  const getValue = () => (
    value ? parseFloat(value) : min || 0
  );

  const updateValue = (val) => {
    let newValue = parseFloat(val);
    if (Number.isNaN(newValue)) {
      // Replace isNaN values with empty string for form validation
      // Fixes warning: `value` prop on `input` should not be null.
      newValue = '';
    }
    if (newValue === '' || (aboveOrEqualMin(newValue) && belowOrEqualMax(newValue))) {
      onChange(props.name, newValue);
    }
  };

  return (
    <label htmlFor={id} className={`${className} ${classes.root} ${inline ? classes.inline : ''}`}>
      <LabelText
        className={`${labelSpanClass} ${classes.label}`}
        screenReaderLabel={screenReaderLabel}
        label={labelText}
        hasError={!!errorMessage}
        dark={dark}
        optional={optional}
      />
      <div className={classes.number}>
        <button
          className={`${classes.btnCtrl} ${classes.btnMinus}`}
          data-qa="btnDown"
          type="button"
          onClick={() => updateValue((getValue(value) - getStep()).toPrecision(10))}
          disabled={disabled || min === value}
        >
          -
        </button>
        <InputComponent
          id={id}
          {...props}
          className={`${inputClass} ${classes.input} `}
          type="number"
          min={min}
          max={max}
          value={(value || value === 0) ? value : ''}
          step={step}
          error={errorMessage}
          inline
          disabled={disabled}
          onChange={e => updateValue(e.target.value)}
        />
        <button
          className={`${classes.btnCtrl} ${classes.btnPlus}`}
          data-qa="btnUp"
          type="button"
          onClick={() => updateValue((getValue() + getStep()).toPrecision(10))}
          disabled={disabled || isAtMax}
        >
          +
        </button>

      </div>
      <Error error={errorMessage} dark={dark} />
    </label>
  );
};

NumberInput.propTypes = {
  /** Name attribute */
  name: PropTypes.string.isRequired,
  /** Text for the span inside the label */
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  /** Custom classes applied to label wrapping component */
  className: PropTypes.string,
  /** Custom classes applied to label span element */
  labelSpanClass: PropTypes.string,
  /** Boolean controlling whether the labelText should only be shown to screen readers */
  screenReaderLabel: PropTypes.bool,
  /** Custom classes applied to input element */
  inputClass: PropTypes.string,
  /** Id for the input if a randomly generated id isn't desired */
  htmlId: PropTypes.string,
  /** Boolean to indicate whether or not you want the standard inline layout */
  inline: PropTypes.bool,
  /** Number value that the input cannot go below */
  min: PropTypes.number,
  /** Number value that the input cannot exceed */
  max: PropTypes.number,
  /** Value to increment with, can by a number or string 'any' */
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Number value of module */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Callback method on change of value */
  onChange: PropTypes.func,
  /** The error to display */
  error: PropTypes.string,
  /** Full errors object. */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** Theme for styling */
  theme: PropTypes.objectOf(PropTypes.any),
  /** dark mode boolean */
  dark: PropTypes.bool,
  /** Shows optional text on label */
  optional: PropTypes.bool,
  /** Disables input field and increment/decrement buttons */
  disabled: PropTypes.bool
};

NumberInput.defaultProps = {
  className: '',
  labelSpanClass: '',
  screenReaderLabel: false,
  inputClass: '',
  inline: false,
  error: '',
  errors: undefined,
  htmlId: null,
  theme: {},
  min: null,
  max: null,
  step: 1,
  value: null,
  onChange: /* istanbul ignore next */ () => {},
  dark: false,
  optional: false,
  disabled: false
};

const StyledNumber = darkModeAware(withStyles(styles)(NumberInput));
const NumberWithInputApi = withInputAPI(StyledNumber);
NumberWithInputApi.displayName = 'Number';
export default NumberWithInputApi;
export const ValidatedNumberInput = NumberWithInputApi;
export const BaseNumberInput = NumberInput;
